import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface CookieConsent {
  accepted: boolean;
  timestamp: number;
  version: string;
}

@Injectable({
  providedIn: 'root'
})
export class CookieConsentService {
  private readonly COOKIE_CONSENT_KEY = 'reno_cookie_consent';
  private readonly CONSENT_VERSION = '1.0';
  
  private consentSubject = new BehaviorSubject<CookieConsent | null>(null);
  public consent$ = this.consentSubject.asObservable();

  constructor() {
    this.loadConsent();
  }

  /**
   * Carrega o consentimento salvo
   */
  private loadConsent(): void {
    const savedConsent = localStorage.getItem(this.COOKIE_CONSENT_KEY);
    if (savedConsent) {
      try {
        const consent: CookieConsent = JSON.parse(savedConsent);
        this.consentSubject.next(consent);
      } catch (error) {
        console.error('Erro ao carregar consentimento de cookies:', error);
        this.clearConsent();
      }
    }
  }

  /**
   * Verifica se o usuário já deu consentimento
   */
  public hasConsent(): boolean {
    const consent = this.consentSubject.value;
    return consent !== null && consent.accepted;
  }

  /**
   * Verifica se o banner deve ser exibido
   */
  public shouldShowBanner(): boolean {
    const consent = this.consentSubject.value;
    const shouldShow = consent === null;
    
    return shouldShow;
  }

  /**
   * Aceita os cookies
   */
  public acceptCookies(): void {
    const consent: CookieConsent = {
      accepted: true,
      timestamp: Date.now(),
      version: this.CONSENT_VERSION
    };
    
    localStorage.setItem(this.COOKIE_CONSENT_KEY, JSON.stringify(consent));
    this.consentSubject.next(consent);
  }

  /**
   * Rejeita os cookies
   */
  public rejectCookies(): void {
    const consent: CookieConsent = {
      accepted: false,
      timestamp: Date.now(),
      version: this.CONSENT_VERSION
    };
    
    localStorage.setItem(this.COOKIE_CONSENT_KEY, JSON.stringify(consent));
    this.consentSubject.next(consent);
    
    // Remove cookies existentes
    this.clearAllCookies();
  }

  /**
   * Limpa o consentimento
   */
  public clearConsent(): void {
    localStorage.removeItem(this.COOKIE_CONSENT_KEY);
    this.consentSubject.next(null);
  }

  /**
   * Limpa todos os cookies quando rejeitados
   */
  private clearAllCookies(): void {
    // Limpa localStorage (exceto o consentimento)
    const keysToKeep = [this.COOKIE_CONSENT_KEY];
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && !keysToKeep.includes(key)) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    // Limpa sessionStorage
    sessionStorage.clear();
    
    // Limpa cookies do documento
    this.clearDocumentCookies();
  }

  /**
   * Limpa cookies do documento
   */
  private clearDocumentCookies(): void {
    const cookies = document.cookie.split(';');
    
    cookies.forEach(cookie => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      
      // Remove o cookie definindo a data de expiração no passado
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname};`;
    });
  }

  /**
   * Verifica se os cookies estão habilitados
   */
  public areCookiesEnabled(): boolean {
    const consent = this.consentSubject.value;
    return consent !== null && consent.accepted;
  }

  /**
   * Obtém o consentimento atual
   */
  public getCurrentConsent(): CookieConsent | null {
    return this.consentSubject.value;
  }
} 